import * as React from 'react';
import { SolutionGridWrapper } from '../../solution-grid-wrapper/solution-grid-wrapper';
import { getSolutionsBaseBySlugs } from '../../../api/api';
import { WithFetcher } from '../../../component-decorators/with-fetcher';
import s from '../search-result/search-result.scss';
import { CenteredLoader } from '../../centered-loader';
import { FluidContainer } from '../../FluidContainer';
import { useConfigContext } from '../../../config-context';
import { Box, EmptyState, Heading } from '@wix/design-system';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { BackButton } from '../../back-button';
import { SearchBoxContainer } from '../../search-box';
import { SolutionGridLayout } from '@wix/app-market-collection-widget';
import { composer } from '../../../component-decorators/composer';
import { TerminatorContainer } from '../terminator';
import { biLogger } from '../../../bi';
import { appMarketSearchResultsPageEmptyStateIsShownSrc24Evid166 } from '@wix/bi-logger-app-market-data/v2';

export interface ISearchResultEmptyStateFetcherProps
  extends Partial<WithTranslationProps> {
  query: string;
  locale?: string;
  countryCode?: string;
  isMobile?: boolean;
  fetchData?: any;
}

const SearchResultEmptyState = composer().compose(
  ({
    t,
    query,
    fetchData,
    locale,
    isMobile,
  }: ISearchResultEmptyStateFetcherProps) => {
    React.useEffect(() => {
      biLogger.report(
        appMarketSearchResultsPageEmptyStateIsShownSrc24Evid166({}),
      );
    }, []);

    return (
      <>
        <FluidContainer
          className={s.pageSettingsGrey}
          dataHook="search-result-empty-state"
          isWide={true}
        >
          <Box
            maxWidth="1248px"
            minWidth={isMobile ? 'none' : '864px'}
            direction="vertical"
            margin="0 auto 33px"
            padding={isMobile ? '0 12px' : '0 48px'}
          >
            <Box align="space-between" marginBottom="12px">
              <Box marginTop="8px">
                <BackButton size="medium" />
              </Box>
              {!isMobile && (
                <Box width="294px">
                  <SearchBoxContainer
                    isInMenu={false}
                    isExpandable={false}
                    isInHeader={false}
                    isInSearchPage={true}
                    locale={locale}
                    value={query}
                    searchLocation="search_result_empty_page"
                  />
                </Box>
              )}
            </Box>
            <EmptyState
              theme="page"
              title={t('searchResultHeaderEmptyState.noResult', { query })}
              subtitle={t('searchResultHeaderEmptyState.tryAgain')}
            />
            <Box
              marginTop={isMobile ? '-32px' : '-38px'}
              marginBottom={isMobile ? '18px' : '9px'}
            >
              <TerminatorContainer
                fullWidth={true}
                term={query}
                isMobile={isMobile}
              />
            </Box>
            <Box marginTop="24px">
              <Heading
                appearance="H2"
                dataHook="search-result-header-recommended"
              >
                {t('searchResultHeader.recommendedApps')}
              </Heading>
            </Box>
            <SolutionGridWrapper
              solutions={fetchData?.suggestions}
              referralSectionName="empty-search"
              layout={SolutionGridLayout.INTEGRATIONS}
            />
          </Box>
        </FluidContainer>
      </>
    );
  },
);

export const SearchResultEmptyStateFetcher = ({
  query,
  t,
}: ISearchResultEmptyStateFetcherProps) => {
  const { locale, countryCode, isMobile } = useConfigContext();
  return (
    <WithFetcher
      loader={<CenteredLoader />}
      initialFetch={() =>
        getSolutionsBaseBySlugs(
          ['staff-picks'],
          locale,
          countryCode,
          true,
          true,
        )
      }
      fetchMemoizationId={`search-result-empty-state-${query}-${locale}`}
    >
      {({ fetchData, reFetch }) => (
        <SearchResultEmptyState
          fetchData={fetchData}
          reFetch={reFetch}
          query={query}
          locale={locale}
          countryCode={countryCode}
          t={t}
          isMobile={isMobile}
        />
      )}
    </WithFetcher>
  );
};
